import type { GeoJSONStoreFeatures } from "terra-draw";

export const TERRA_DRAW_COORDINATE_PRECISION = 7;

const sameCoordinate = (
  first: google.maps.LatLngLiteral,
  second: google.maps.LatLngLiteral,
) => first.lat === second.lat && first.lng === second.lng;

const EARTH_RADIUS_METERS = 6_371_000;

function distanceInMeters(
  first: google.maps.LatLngLiteral,
  second: google.maps.LatLngLiteral,
): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(second.lat - first.lat);
  const longitudeDelta = toRadians(second.lng - first.lng);
  const firstLatitude = toRadians(first.lat);
  const secondLatitude = toRadians(second.lat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(firstLatitude) *
      Math.cos(secondLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;

  return (
    2 *
    EARTH_RADIUS_METERS *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

function extractLatLngCoordinates(
  value: unknown,
  depth = 0,
): google.maps.LatLngLiteral[] {
  if (depth > 4 || value === null || value === undefined) return [];

  if (typeof value === "string") {
    try {
      return extractLatLngCoordinates(JSON.parse(value), depth + 1);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return [];

    const objectCoordinates = value
      .map((coordinate) => {
        if (!coordinate || typeof coordinate !== "object") return null;
        const point = coordinate as Record<string, unknown>;
        const lat = Number(point.lat);
        const lng = Number(point.lng ?? point.lon);
        return { lat, lng };
      })
      .filter(
        (coordinate): coordinate is google.maps.LatLngLiteral =>
          coordinate !== null &&
          Number.isFinite(coordinate.lat) &&
          Number.isFinite(coordinate.lng),
      );

    if (objectCoordinates.length === value.length) return objectCoordinates;

    const numericPairs = value
      .map((coordinate) => {
        if (
          !Array.isArray(coordinate) ||
          coordinate.length < 2 ||
          !Number.isFinite(Number(coordinate[0])) ||
          !Number.isFinite(Number(coordinate[1]))
        ) {
          return null;
        }
        return { lat: Number(coordinate[1]), lng: Number(coordinate[0]) };
      })
      .filter(
        (coordinate): coordinate is google.maps.LatLngLiteral =>
          coordinate !== null,
      );

    if (numericPairs.length === value.length) return numericPairs;

    return extractLatLngCoordinates(value[0], depth + 1);
  }

  if (typeof value === "object") {
    const object = value as Record<string, unknown>;
    if ("coordinates" in object) {
      return extractLatLngCoordinates(object.coordinates, depth + 1);
    }
  }

  return [];
}

export function normalizeLatLngPolygon(
  coordinates: google.maps.LatLngLiteral[],
): google.maps.LatLngLiteral[] {
  const normalized = coordinates
    .map(({ lat, lng }) => ({ lat: Number(lat), lng: Number(lng) }))
    .filter(
      ({ lat, lng }) =>
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180,
    )
    .filter(
      (coordinate, index, values) =>
        index === 0 || !sameCoordinate(coordinate, values[index - 1]),
    );

  if (
    normalized.length > 1 &&
    sameCoordinate(normalized[0], normalized[normalized.length - 1])
  ) {
    normalized.pop();
  }

  return normalized;
}

export function branchCoverageToLatLng(
  serviceZone: unknown,
): google.maps.LatLngLiteral[] {
  const coordinates = normalizeLatLngPolygon(
    extractLatLngCoordinates(serviceZone),
  );
  return coordinates.length >= 3 ? coordinates : [];
}

export function removePolygonVertexNear(
  coordinates: google.maps.LatLngLiteral[],
  target: google.maps.LatLngLiteral,
  maximumDistanceMeters: number,
): google.maps.LatLngLiteral[] | null {
  const normalized = normalizeLatLngPolygon(coordinates);
  if (normalized.length <= 3) return null;

  let nearestIndex = -1;
  let nearestDistance = Number.POSITIVE_INFINITY;
  normalized.forEach((coordinate, index) => {
    const distance = distanceInMeters(coordinate, target);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });

  if (nearestIndex < 0 || nearestDistance > maximumDistanceMeters) return null;
  return normalized.filter((_, index) => index !== nearestIndex);
}

export function latLngToTerraDrawPolygon(
  coordinates: google.maps.LatLngLiteral[],
  id: string | number,
): GeoJSONStoreFeatures | null {
  const precisionFactor = 10 ** TERRA_DRAW_COORDINATE_PRECISION;
  const normalized = normalizeLatLngPolygon(
    coordinates.map(({ lat, lng }) => ({
      lat: Math.round(lat * precisionFactor) / precisionFactor,
      lng: Math.round(lng * precisionFactor) / precisionFactor,
    })),
  );
  if (normalized.length < 3) return null;

  const ring = normalized.map(({ lat, lng }) => [lng, lat]);
  ring.push([...ring[0]]);

  return {
    id,
    type: "Feature",
    properties: { mode: "polygon" },
    geometry: { type: "Polygon", coordinates: [ring] },
  } as GeoJSONStoreFeatures;
}

export function latLngToServiceZonePolygon(
  coordinates: google.maps.LatLngLiteral[],
): Array<{ lat: number; lon: number }> | null {
  const normalized = normalizeLatLngPolygon(coordinates);
  if (normalized.length < 3) return null;

  const serviceZone = normalized.map(({ lat, lng }) => ({ lat, lon: lng }));
  serviceZone.push({ ...serviceZone[0] });
  return serviceZone;
}

export function terraDrawPolygonToLatLng(
  feature: GeoJSONStoreFeatures | undefined,
): google.maps.LatLngLiteral[] | null {
  if (!feature || feature.geometry.type !== "Polygon") return null;

  const outerRing = feature.geometry.coordinates[0];
  if (!outerRing || outerRing.length < 4) return null;

  const coordinates = normalizeLatLngPolygon(
    outerRing.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) })),
  );

  return coordinates.length >= 3 ? coordinates : null;
}
